<template>
  <div class="home">
    <!-- 顶部导航栏 -->
    <header class="header">
      <div class="header-container">
        <div class="logo-section">
          <div class="logo">
            <img src="@/assets/logo.png" alt="Logo">
          </div>
          <div class="company-name">world通信合同会社</div>
        </div>
        <!-- PC端导航菜单 -->
        <nav class="nav">
          <div class="nav-item" 
               v-for="(item, index) in navItems" 
               :key="index"
               @click="scrollToSection(item.link)">
            {{ item.text }}
          </div>
        </nav>
        <!-- 移动端菜单按钮 -->
        <div class="menu-icon" @click="toggleMenu">
          <img v-if="!isMenuOpen" src="@/assets/icon/menu.png" alt="菜单">
          <img v-else src="@/assets/icon/close.png" alt="关闭">
        </div>
      </div>
    </header>

    <!-- 轮播图 -->
    <div class="banner" 
         @touchstart="touchStart" 
         @touchmove="touchMove" 
         @touchend="touchEnd">
      <div class="banner-container">
        <div class="banner-wrapper" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
          <div v-for="(slide, index) in slides" :key="index" class="banner-slide">
            <img :src="slide.image" :alt="slide.title">
            <div class="banner-content">
              <h2>{{ slide.title }}</h2>
              <p>{{ slide.description }}</p>
              <button class="learn-more" @click="scrollToSection('#about')">詳しく見る</button>
            </div>
          </div>
        </div>
        <!-- 轮播图指示器 -->
        <div class="banner-indicators">
          <span 
            v-for="(_, index) in slides" 
            :key="index"
            :class="{ active: currentIndex === index }"
            @click="setSlide(index)"
          ></span>
        </div>
        <!-- 轮播图按钮 -->
        <button class="banner-btn prev" @click="prevSlide">&lt;</button>
        <button class="banner-btn next" @click="nextSlide">&gt;</button>
      </div>
    </div>

    <!-- 推荐信息 -->
    <div class="recommend">
      <h2 class="section-title">推薦荐情報</h2>
      <div class="recommend-container">
        <!-- 大轮播图 -->
        <div class="recommend-banner"
             @touchstart="recommendTouchStart"
             @touchmove="recommendTouchMove"
             @touchend="recommendTouchEnd">
          <div class="banner-wrapper" :style="{ transform: `translateX(-${recommendIndex * 100}%)` }">
            <div v-for="(item, index) in recommendSlides" :key="index" class="banner-slide">
              <img :src="item.image" :alt="item.title">
              <div class="content">
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
                <button class="more-btn" @click="scrollToSection(index === 0 ? '#products' : '#computer')">詳しく見る ></button>
              </div>
            </div>
          </div>
          <!-- 指示器 -->
          <div class="indicators">
            <span 
              v-for="(_, index) in recommendSlides" 
              :key="index"
              :class="{ active: recommendIndex === index }"
              @click="setRecommendSlide(index)"
            ></span>
          </div>
        </div>

        <!-- 展示卡片 -->
        <div class="card-container">
          <div v-for="(card, index) in cards" :key="index" class="card">
            <img :src="card.image" :alt="card.title">
            <div class="card-content">
              <h3>{{ card.title }}</h3>
              <p>{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 产品展示 -->
    <div class="products-section" id="products">
      <!-- 手机产品 -->
      <div class="product-category">
        <h2 class="section-title">スマートフォン</h2>
        <div class="product-grid">
          <div class="product-card" v-for="(product, index) in phoneProducts" :key="'phone-'+index">
            <div class="product-image">
              <img :src="product.image" :alt="product.name">
            </div>
            <div class="product-info">
              <h3 class="product-name">{{ product.name }}</h3>
              <p class="product-desc">{{ product.description }}</p>
              <div class="product-price">{{ product.price }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 游戏机 -->
      <div class="product-category" id="playstation">
        <h2 class="section-title">ゲーム機</h2>
        <div class="product-grid">
          <div class="product-card" v-for="(product, index) in playstationProducts" :key="'playstation-'+index">
            <div class="product-image">
              <img :src="product.image" :alt="product.name">
            </div>
            <div class="product-info">
              <h3 class="product-name">{{ product.name }}</h3>
              <p class="product-desc">{{ product.description }}</p>
              <div class="product-price">{{ product.price }}</div>
            </div>
          </div>
        </div>
      </div>


      <!-- ipad -->
      <div class="product-category" id="ipad">
        <h2 class="section-title">iPad</h2>
        <div class="product-grid">
          <div class="product-card" v-for="(product, index) in ipadProducts" :key="'ipad-'+index">
            <div class="product-image">
              <img :src="product.image" :alt="product.name">
            </div>
            <div class="product-info">
              <h3 class="product-name">{{ product.name }}</h3>
              <p class="product-desc">{{ product.description }}</p>
              <div class="product-price">{{ product.price }}</div>
            </div>
          </div>
        </div>
      </div>
      

      <!-- 电脑产品 -->
      <div class="product-category" id="computer">
        <h2 class="section-title">ノートパソコン</h2>
        <div class="product-grid">
          <div class="product-card" v-for="(product, index) in computerProducts" :key="'computer-'+index">
            <div class="product-image">
              <img :src="product.image" :alt="product.name">
            </div>
            <div class="product-info">
              <h3 class="product-name">{{ product.name }}</h3>
              <p class="product-desc">{{ product.description }}</p>
              <div class="product-price">{{ product.price }}</div>
            </div>
          </div>
        </div>
      </div>


    </div>
    
    <!-- 公司信息 -->
    <div class="company-info-section">
      <!-- 业务简介 -->
      <div class="business-intro" id="business">
        <div class="content-wrapper">
          <h2 class="section-title">事業内容</h2>
          <div class="intro-text">
            <p class="highlight">スマートフォン・電気製品・買取・輸出入業務</p>
            <p>world通信合同会社はスマーフォン・ゲーム機・カメラ・パソコンなど電気製品を買取、販売する専門商社です。</p>
          </div>
        </div>
      </div>

      <!-- 公司简介 -->
      <div class="about-company" id="about">
        <div class="content-wrapper">
          <h2 class="section-title">自社概要</h2>
          <div class="about-content">
            <div class="text-content">
              <p>当社は2016年10月に創業し、スマートフォンやタブレット端末を中心とした電気機器関連事業を展開しています。事業所は埼玉県川口市にあり、代表1名と数名の従業員で運営しています。</p>
              <p>2024年の売上実績は５億円に達しました。この素晴らしい成果を成し遂げることができたのは、常に当社を信頼していただき、支えていただいているお客様方のおかげです。</p>
              <p>お客様に心から満足していただき、信頼される会社であり続けるために、お客様のご要望と社会の経営環境の変化に迅速に対応していきます。</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 详细信息 -->
      <div class="company-details">
        <div class="content-wrapper">
          <h2 class="section-title">会社概要</h2>
          <div class="info-list">
            <div class="info-item">
              <div class="info-label">会社名</div>
              <div class="info-content">world通信合同会社</div>
            </div>
            <div class="info-item">
              <div class="info-label">事業内容</div>
              <div class="info-content">スマートフォン・家電の買取、販売及び海外輸出入</div>
            </div>
            <div class="info-item">
              <div class="info-label">所在地</div>
              <div class="info-content">
                〒332-0034<br>
                埼玉県川口市並木3‐13‐4　ダイドービル弐番館　201号室
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">TEL</div>
              <div class="info-content">04-8255-5466</div>
            </div>
            <div class="info-item">
              <div class="info-label">設立年月日</div>
              <div class="info-content">2016年10月</div>
            </div>
            <div class="info-item">
              <div class="info-label">資本金</div>
              <div class="info-content">500万円</div>
            </div>
            <div class="info-item">
              <div class="info-label">古物許可証</div>
              <div class="info-content">
                埼玉県公安委員会許可<br>
                第431030054758号
              </div>
            </div>
          </div>
          
          <!-- 公司照片展示 -->
          <div class="company-photos">
            <div class="photo-grid">
              <div class="photo-item">
                <img src="@/assets/company1.jpg" alt="公司照片1">
                <div class="photo-caption">会社外観</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 联系我们 -->
    <div class="contact-section" id="contact">
      <div class="content-wrapper">
        <h2 class="section-title">お問い合わせ</h2>
        <div class="contact-container">
          <div class="contact-info">
            <h3>お気軽にお問い合わせください</h3>
            <p>製品のご購入や買取についてのご質問、その他のお問い合わせは下記フォームよりご連絡ください。</p>
            <div class="contact-method">
              <div class="method-item">
                <i class="phone-icon">📞</i>
                <div class="method-text">
                  <h4>お電話</h4>
                  <p>04-8255-5466</p>
                </div>
              </div>
              <div class="method-item">
                <i class="email-icon">✉️</i>
                <div class="method-text">
                  <h4>メール</h4>
                  <p>zrm@worldtxht.com</p>
                </div>
              </div>
            </div>
          </div>
          
          <form class="contact-form" @submit.prevent="submitForm">
            <div class="form-group" :class="{ 'has-error': errors.name }">
              <label>お名前 <span class="required">*</span></label>
              <input 
                type="text" 
                v-model="formData.name" 
                :class="{ 'error': errors.name }"
                @focus="clearError('name')"
                placeholder="例：山田 太郎"
              >
              <span class="error-message" v-if="errors.name">{{ errors.name }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.email }">
              <label>メールアドレス <span class="required">*</span></label>
              <input 
                type="email" 
                v-model="formData.email"
                :class="{ 'error': errors.email }"
                @focus="clearError('email')"
                placeholder="例：example@email.com"
              >
              <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.phone }">
              <label>電話番号</label>
              <input 
                type="tel" 
                v-model="formData.phone"
                :class="{ 'error': errors.phone }"
                @focus="clearError('phone')"
                placeholder="例：080-1234-5678"
              >
              <span class="error-message" v-if="errors.phone">{{ errors.phone }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.message }">
              <label>お問い合わせ内容 <span class="required">*</span></label>
              <textarea 
                v-model="formData.message"
                :class="{ 'error': errors.message }"
                @focus="clearError('message')"
                rows="4"
                placeholder="お問い合わせ内容をご記入ください"
              ></textarea>
              <span class="error-message" v-if="errors.message">{{ errors.message }}</span>
            </div>

            <button 
              type="submit" 
              class="submit-btn" 
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">送信する</span>
              <span v-else class="loading-spinner"></span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- 移动端菜单 -->
    <div class="sub-menu" :class="{ 'sub-menu-open': isMenuOpen }">
      <div class="menu-items">
        <div class="menu-item" 
             v-for="(item, index) in navItems" 
             :key="index" 
             @click="handleMenuClick(item)">
          {{ item.text }}
          <img src="@/assets/icon/arrow.png" alt="箭头" class="arrow-icon">
        </div>
      </div>
    </div>

    <!-- 提示框 -->
    <div class="message-popup" v-if="messagePopup.show">
      <div class="message-content" :class="messagePopup.type">
        <div class="message-icon">
          {{ messagePopup.type === 'success' ? '✓' : '!' }}
        </div>
        <div class="message-text">{{ messagePopup.message }}</div>
      </div>
    </div>

    <!-- 回到顶部按钮 -->
    <div class="back-to-top" v-show="showBackToTop" @click="scrollToTop">
      <img src="@/assets/icon/top.png" alt="トップへ">
    </div>

    <!-- 联系悬浮按钮 -->
    <div class="contact-float" @click="showContactDialog = true">
      <img src="@/assets/icon/contact.png" alt="お問い合わせ">
    </div>

    <!-- 联系对话框 -->
    <div class="dialog-overlay" v-if="showContactDialog" @click="showContactDialog = false">
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h2>お問い合わせ</h2>
          <button class="close-btn" @click="showContactDialog = false">×</button>
        </div>
        <div class="dialog-body">
          <div class="contact-info">
            <h3>お気軽にお問い合わせください</h3>
            <p>製品のご購入や買取についてのご質問、その他のお問い合わせは下記フォームよりご連絡ください。</p>
            <div class="contact-method">
              <div class="method-item">
                <i class="phone-icon">📞</i>
                <div class="method-text">
                  <h4>お電話</h4>
                  <p>04-8255-5466</p>
                </div>
              </div>
              <div class="method-item">
                <i class="email-icon">✉️</i>
                <div class="method-text">
                  <h4>メール</h4>
                  <p>zrm@worldtxht.com</p>
                </div>
              </div>
            </div>
          </div>
          
          <form class="contact-form" @submit.prevent="submitForm">
            <div class="form-group" :class="{ 'has-error': errors.name }">
              <label>お名前 <span class="required">*</span></label>
              <input 
                type="text" 
                v-model="formData.name" 
                :class="{ 'error': errors.name }"
                @focus="clearError('name')"
                placeholder="例：山田 太郎"
              >
              <span class="error-message" v-if="errors.name">{{ errors.name }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.email }">
              <label>メールアドレス <span class="required">*</span></label>
              <input 
                type="email" 
                v-model="formData.email"
                :class="{ 'error': errors.email }"
                @focus="clearError('email')"
                placeholder="例：example@email.com"
              >
              <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.phone }">
              <label>電話番号</label>
              <input 
                type="tel" 
                v-model="formData.phone"
                :class="{ 'error': errors.phone }"
                @focus="clearError('phone')"
                placeholder="例：080-1234-5678"
              >
              <span class="error-message" v-if="errors.phone">{{ errors.phone }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.message }">
              <label>お問い合わせ内容 <span class="required">*</span></label>
              <textarea 
                v-model="formData.message"
                :class="{ 'error': errors.message }"
                @focus="clearError('message')"
                rows="4"
                placeholder="お問い合わせ内容をご記入ください"
              ></textarea>
              <span class="error-message" v-if="errors.message">{{ errors.message }}</span>
            </div>

            <button 
              type="submit" 
              class="submit-btn" 
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">送信する</span>
              <span v-else class="loading-spinner"></span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-main">
          <div class="footer-info">
            <div class="footer-logo">
              <img src="@/assets/logo.png" alt="Logo">
              <span class="company-name">world通信合同会社</span>
            </div>
            <p class="company-slogan">信頼と品質、お客様第一</p>
          </div>
          <div class="footer-contact">
            <h3>お問い合わせ</h3>
            <div class="contact-details">
              <p>📪 332-0034 </p>
              <p>📍 埼玉县川口市並木3-13-4</br>ダイドービル弐番館 201 号室 </p>
              <p>📞 04-8255-5466</p>
              <p>✉️ zrm@worldtxht.com</p>
            </div>
          </div>
          <div class="footer-hours">
            <h3>営業時間</h3>
            <div class="hours-details">
              <p>月曜日〜金曜日:10:00〜19:00</p>
              <p>土、日曜日・祝日休業: 休業</p>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <div class="copyright">
            <p>&copy; 2016-2025 world通信合同会社 All Rights Reserved.</p>
          </div>
          <div class="footer-links">
            <a href="#">プライバシーポリシー</a>
            <span class="divider">|</span>
            <a href="#">利用規約</a>
            <span class="divider">|</span>
            <a href="#">特定商取引法に基づく表記</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      navItems: [
        { text: '個人・家庭向け製品', link: '#products' },
        { text: 'サポート', link: '#business' },
        { text: 'パートナー', link: '#contact' },
        { text: '会社概要', link: '#about' }
      ],
      isMenuOpen: false,
      currentIndex: 0,
      slides: [
        {
          title: 'world通信合同会社',
          description: 'スマートデバイスの優れた販売企業',
          image: require('@/assets/banner1.jpg')
        },
        {
          title: 'デジタル化の普及：世界をつなぎ、生活を豊かに',
          description: 'デジタルの力で、人々の暮らしをより便利に',
          image: require('@/assets/banner2.jpg')
        },
        {
          title: '優れた信頼性',
          description: '卓越した信頼性と比類なきサービス品質で、デジタル変革をサポート',
          image: require('@/assets/banner3.jpg')
        }
      ],
      timer: null,
      touchStartX: 0,
      touchEndX: 0,
      recommendIndex: 0,
      recommendTimer: null,
      recommendTouchStartX: 0,
      recommendTouchEndX: 0,
      recommendSlides: [
        {
          title: 'スマートフォン販売',
          description: '高品質・低価格でスマートライフを始めよう',
          image: require('@/assets/recommend1.jpg')
        },
        {
          title: '高性能ノートパソコン',
          description: '新しい生活体験をご提供',
          image: require('@/assets/recommend2.jpg')
        }
      ],
      cards: [
        {
          title: '大顧客向け',
          description: '便利な生活をご提供',
          image: require('@/assets/card1.jpg')
        },
        {
          title: 'オンラインストア',
          description: '新しいショッピング体験',
          image: require('@/assets/card2.jpg')
        },
        {
          title: 'スマート時代',
          description: 'スマート社会の創造',
          image: require('@/assets/card3.jpg')
        },
        {
          title: '協力者',
          description: 'スマートエコシステムの',
          image: require('@/assets/card4.jpg')
        }
      ],
      phoneProducts: [
        {
          name: 'iPhone 16se',
          description: '新品',
          price: 'ご相談ください',
          image: require('@/assets/products/phone1.jpg')
        },
        {
          name: 'iPhone 13',
          description: '新品',
          price: 'ご相談ください',
          image: require('@/assets/products/phone2.jpg')
        },
        {
          name: 'iphone',
          description: '中古',
          price: 'ご相談ください',
          image: require('@/assets/products/phone3.jpg')
        },
        {
          name: 'iphone',
          description: '中古',
          price: 'ご相談ください',
          image: require('@/assets/products/phone4.jpg')
        }
      ],
      playstationProducts: [
        {
          name: 'PS5',
          description: '大量出荷',
          price: 'ご相談ください',
          image: require('@/assets/products/playstation1.jpg')
        },
        {
          name: 'PS5',
          description: '大量出荷',
          price: 'ご相談ください',
          image: require('@/assets/products/playstation2.jpg')
        },
      ],
      ipadProducts: [
        {
          name: 'ipad',
          description: '新品/中古',
          price: 'ご相談ください',
          image: require('@/assets/products/ipad1.jpg')
        },
      ],
      computerProducts: [
        {
          name: 'MacBook Pro',
          description: '新品/中古',
          price: 'ご相談ください',
          image: require('@/assets/products/computer1.jpg')
        },
        {
          name: 'MacBook Air',
          description: '新品/中古',
          price: 'ご相談ください',
          image: require('@/assets/products/computer2.jpg')
        },
      ],
      formData: {
        name: '',
        email: '',
        phone: '',
        message: ''
      },
      errors: {
        name: '',
        email: '',
        phone: '',
        message: ''
      },
      isSubmitting: false,
      messagePopup: {
        show: false,
        message: '',
        type: 'success'
      },
      showBackToTop: false,
      showContactDialog: false
    }
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      if (this.isMenuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    handleMenuClick(item) {
      this.scrollToSection(item.link);
      this.isMenuOpen = false;
      document.body.style.overflow = '';
    },
    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    },
    prevSlide() {
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    },
    setSlide(index) {
      this.currentIndex = index;
    },
    startAutoPlay() {
      this.timer = setInterval(this.nextSlide, 3000);
    },
    stopAutoPlay() {
      if (this.timer) {
        clearInterval(this.timer);
      }
    },
    touchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.stopAutoPlay(); // 触摸时停止自动播放
    },
    touchMove(e) {
      this.touchEndX = e.touches[0].clientX;
    },
    touchEnd() {
      const diff = this.touchStartX - this.touchEndX;
      const threshold = 50; // 滑动阈值

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          // 向左滑动
          this.nextSlide();
        } else {
          // 向右滑动
          this.prevSlide();
        }
      }
      this.startAutoPlay(); // 触摸结束后重新开始自动播放
    },
    setRecommendSlide(index) {
      this.recommendIndex = index;
    },
    startRecommendAutoPlay() {
      this.recommendTimer = setInterval(() => {
        this.recommendIndex = (this.recommendIndex + 1) % this.recommendSlides.length;
      }, 3000);
    },
    stopRecommendAutoPlay() {
      if (this.recommendTimer) {
        clearInterval(this.recommendTimer);
      }
    },
    recommendTouchStart(e) {
      this.recommendTouchStartX = e.touches[0].clientX;
      this.stopRecommendAutoPlay();
    },
    recommendTouchMove(e) {
      this.recommendTouchEndX = e.touches[0].clientX;
    },
    recommendTouchEnd() {
      const diff = this.recommendTouchStartX - this.recommendTouchEndX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.recommendIndex = (this.recommendIndex + 1) % this.recommendSlides.length;
        } else {
          this.recommendIndex = (this.recommendIndex - 1 + this.recommendSlides.length) % this.recommendSlides.length;
        }
      }
      this.startRecommendAutoPlay();
    },
    validateForm() {
      let isValid = true;
      this.errors = {
        name: '',
        email: '',
        phone: '',
        message: ''
      };

      if (!this.formData.name.trim()) {
        this.errors.name = 'お名前を入力してください';
        isValid = false;
      }

      if (!this.formData.email.trim()) {
        this.errors.email = 'メールアドレスを入力してください';
        isValid = false;
      } else if (!this.validateEmail(this.formData.email)) {
        this.errors.email = '有効なメールアドレスを入力してください';
        isValid = false;
      }

      if (!this.formData.message.trim()) {
        this.errors.message = 'お問い合わせ内容を入力してください';
        isValid = false;
      }

      return isValid;
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },

    validatePhone(phone) {
      const re = /^[0-9-]{10,}$/;
      return re.test(phone);
    },

    clearError(field) {
      this.errors[field] = '';
    },

    showMessage(message, type = 'success') {
      this.messagePopup = {
        show: true,
        message,
        type
      };

      // 3秒后自动关闭
      setTimeout(() => {
        this.messagePopup.show = false;
      }, 3000);
    },

    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      try {
        // 构建查询参数
        const queryParams = new URLSearchParams({
          name: this.formData.name,
          email: this.formData.email,
          phone: this.formData.phone,
          message: this.formData.message
        });

        // 发送GET请求
        const response = await fetch(`/api/message?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });

        // 解析响应数据
        const data = await response.json();
        
        // 成功提交后清空表单
        this.formData = {
          name: '',
          email: '',
          phone: '',
          message: ''
        };

        // 显示成功消息
        this.showMessage('お問い合わせありがとうございます。');
        
        // 关闭对话框
        this.showContactDialog = false;
        
      } catch (error) {
        // 错误处理
        console.error('提交失败:', error);
        this.showMessage('送信に失敗しました。もう一度お試しください。', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    scrollToSection(link) {
      const element = document.querySelector(link);
      if (element) {
        // 考虑固定导航栏的高度
        const offset = 60; // 导航栏高度
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    handleScroll() {
      this.showBackToTop = window.scrollY > 300;
    }
  },
  mounted() {
    this.startAutoPlay();
    this.startRecommendAutoPlay();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    this.stopAutoPlay();
    this.stopRecommendAutoPlay();
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style lang="less" scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  z-index: 1000;
  height: 60px; // 固定高度

  .header-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .logo-section {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .logo img {
    height: 40px;
  }

  .company-name {
    font-size: 22px;
    color: #333;
    font-weight: bold;
  }

  .nav {
    display: flex;
    gap: 30px;

    .nav-item {
      cursor: pointer;
      font-size: 16px;
      color: #333;
      padding: 8px 0;
      position: relative;

      &:hover {
        color: #c7000b;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: #c7000b;
        transform: scaleX(0);
        transition: transform 0.3s ease;
      }

      &:hover::after {
        transform: scaleX(1);
      }
    }
  }

  .menu-icon {
    display: none;
    cursor: pointer;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100%;
      height: 100%;
    }
  }
}

.sub-menu {
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 999;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;

  &.sub-menu-open {
    transform: translateX(0);
  }

  .menu-items {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;

    .menu-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 15px 0;
      border-bottom: 1px solid #eee;
      cursor: pointer;
      font-size: 16px;
      color: #333;

      &:hover {
        color: #c7000b;
      }

      .arrow-icon {
        width: 16px;
        height: 16px;
      }
    }
  }
}

.banner {
  width: 100%;
  height: calc(100vh - 60px); // 使用视口高度减去导航栏高度
  max-height: 600px; // 设置最大高度
  position: relative;
  overflow: hidden;
  touch-action: pan-y pinch-zoom; // 优化移动端触摸体验

  .banner-container {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .banner-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    transition: transform 0.3s ease-out; // 加快过渡速度
    will-change: transform; // 优化性能
  }

  .banner-slide {
    min-width: 100%;
    height: 100%;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .banner-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #fff;
      width: 80%;
      max-width: 800px;

      h2 {
        font-size: 36px;
        margin-bottom: 20px;
        font-weight: bold;
      }

      p {
        font-size: 18px;
        margin-bottom: 30px;
        text-align: center; 
      }

      .learn-more {
        padding: 12px 30px;
        font-size: 16px;
        color: #fff;
        background-color: #c7000b;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.3s;

        &:hover {
          background-color: #a00008;
        }
      }
    }
  }

  .banner-indicators {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 10px;

    span {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.5);
      cursor: pointer;
      transition: background-color 0.3s;

      &.active {
        background-color: #fff;
      }
    }
  }

  .banner-btn {
    position: absolute;
    bottom: 30px;
    transform: translateY(0);
    width: 40px;
    height: 40px;
    background-color: rgba(0, 0, 0, 0.3);
    color: #fff;
    border: none;
    border-radius: 50%;
    font-size: 20px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.5);
    }

    &.prev {
      left: 20px;
    }

    &.next {
      right: 20px;
    }
  }
}

.recommend {
  padding: 40px 0;
  background-color: #fff;

  .section-title {
    text-align: center;
    font-size: 28px;
    color: #333;
    margin-bottom: 30px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 2px;
      background-color: #c7000b;
    }
  }

  .recommend-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px 0 20px;
    background-color: #fff;
  }

  .recommend-banner {
    width: 100%;
    height: 400px;
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    margin-bottom: 30px;

    .banner-wrapper {
      width: 100%;
      height: 100%;
      display: flex;
      transition: transform 0.3s ease-out;

      .banner-slide {
        min-width: 100%;
        height: 100%;
        position: relative;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 30px;
          background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
          color: #fff;

          h3 {
            font-size: 24px;
            margin-bottom: 10px;
          }

          p {
            font-size: 16px;
            margin-bottom: 15px;
          }

          .more-btn {
            background: none;
            border: none;
            color: #fff;
            font-size: 16px;
            cursor: pointer;
            padding: 0;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }

    .indicators {
      position: absolute;
      bottom: 20px;
      right: 20px;
      display: flex;
      gap: 8px;

      span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: rgba(255,255,255,0.5);
        cursor: pointer;
        transition: background-color 0.3s;

        &.active {
          background-color: #fff;
        }
      }
    }
  }

  .card-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    background-color: #fff;

    .card {
      background: #fff;
      border-radius: 8px;
      overflow: hidden;
      transition: transform 0.3s;
      cursor: pointer;

      &:hover {
        transform: translateY(-5px);
      }

      img {
        width: 100%;
        height: 160px;
        object-fit: cover;
        display: block;
        margin-bottom: 0;
      }

      .card-content {
        padding: 15px;
        background-color: #f5f5f5;
        margin-top: 0;

        h3 {
          font-size: 18px;
          color: #333;
          margin-bottom: 8px;
        }

        p {
          font-size: 14px;
          color: #666;
          line-height: 1.4;
        }
      }
    }
  }
}

.company-info-section {
  background-color: #fff;

  .content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    padding: 60px 09px;
  }

  .section-title {
    font-size: 32px;
    color: #333;
    margin-bottom: 30px;
    text-align: center;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 2px;
      background-color: #c7000b;
    }
  }

  // 业务简介样式
  .business-intro {
    background-color: #f8f8f8;
    
    .intro-text {
      text-align: center;
      max-width: 800px;
      margin: 0 auto;

      .highlight {
        font-size: 24px;
        color: #c7000b;
        margin-bottom: 20px;
        font-weight: bold;
      }

      p {
        font-size: 18px;
        line-height: 1.6;
        margin-bottom: 15px;
        color: #333;
      }
    }
  }

  // 公司简介样式
  .about-company {
    background-color: #fff;

    .about-content {
      max-width: 800px;
      margin: 0 auto;

      .text-content {
        p {
          font-size: 16px;
          line-height: 1.8;
          margin-bottom: 20px;
          color: #666;
          text-align: justify;
        }
      }
    }
  }

  // 详细信息样式
  .company-details {
    background-color: #f8f9fc;
    padding: 60px 0;

    .content-wrapper {
      max-width: 800px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .info-list {
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
      overflow: hidden;

      .info-item {
        display: flex;
        padding: 24px 30px;
        transition: background-color 0.3s ease;

        &:not(:last-child) {
          border-bottom: 1px solid #eef0f5;
        }

        &:hover {
          background-color: #f8faff;
        }

        .info-label {
          width: 140px;
          flex-shrink: 0;
          font-weight: 600;
          color: #1a1a1a;
          position: relative;
          padding-left: 15px;
          text-align: left;
          font-size: 15px;
          letter-spacing: 0.5px;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 6px;
            height: 6px;
            background-color: #c7000b;
            border-radius: 50%;
          }
        }

        .info-content {
          flex: 1;
          color: #4a4a4a;
          line-height: 1.8;
          text-align: left;
          font-size: 15px;
          letter-spacing: 0.3px;
          padding-left: 20px;
          position: relative;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 1px;
            background: linear-gradient(to bottom, transparent, #eef0f5 20%, #eef0f5 80%, transparent);
          }
        }
      }
    }

    .company-photos {
      margin-top: 40px;

      .photo-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        
        .photo-item {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
          transition: transform 0.3s ease;

          &:hover {
            transform: translateY(-5px);
          }

          img {
            width: 100%;
            height: 300px;
            object-fit: cover;
            display: block;
          }

          .photo-caption {
            padding: 15px;
            text-align: center;
            font-size: 16px;
            color: #333;
            background: #fff;
          }
        }
      }
    }
  }
}

.products-section {
  padding: 60px 0;
  background-color: #fff;

  .product-category {
    max-width: 1200px;
    margin: 0 auto 60px;
    padding: 0 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .section-title {
    text-align: center;
    font-size: 32px;
    color: #333;
    margin-bottom: 40px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 2px;
      background-color: #c7000b;
    }
  }

  .product-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  .product-card {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    }

    .product-image {
      width: 100%;
      position: relative;
      padding-bottom: 100%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #fff;

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% - 40px);
        height: calc(100% - 40px);
        object-fit: contain;
        border-radius: 12px;    
        transition: transform 0.3s ease;
      }

      &:hover img {
        transform: translate(-50%, -50%) scale(1.05);
      }
    }

    .product-info {
      padding: 20px;
      background: #fff;

      .product-name {
        font-size: 20px;
        color: #333;
        margin-bottom: 8px;
        font-weight: 600;
      }

      .product-desc {
        font-size: 14px;
        color: #666;
        margin-bottom: 12px;
        line-height: 1.4;
      }

      .product-price {
        font-size: 18px;
        color: #c7000b;
        font-weight: 600;
      }
    }
  }
}

.contact-section {
  background-color: #f8f9fc;
  padding: 80px 0;

  .content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .contact-container {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 60px;
    margin-top: 40px;
  }

  .contact-info {
    h3 {
      font-size: 24px;
      color: #333;
      margin-bottom: 20px;
    }

    p {
      font-size: 16px;
      color: #666;
      line-height: 1.6;
      margin-bottom: 30px;
    }

    .contact-method {
      .method-item {
        display: flex;
        align-items: center;
        margin-bottom: 20px;

        i {
          font-size: 24px;
          margin-right: 15px;
          color: #c7000b;
        }

        .method-text {
          h4 {
            font-size: 16px;
            color: #333;
            margin-bottom: 5px;
          }

          p {
            font-size: 15px;
            color: #666;
            margin: 0;
          }
        }
      }
    }
  }

  .contact-form {
    background: #fff;
    padding: 40px;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

    .form-group {
      margin-bottom: 25px;

      label {
        display: block;
        font-size: 15px;
        color: #333;
        margin-bottom: 8px;
        font-weight: 500;

        .required {
          color: #c7000b;
          margin-left: 4px;
        }
      }

      input, textarea {
        width: 100%;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 15px;
        transition: all 0.3s ease;

        &:focus {
          outline: none;
          border-color: #c7000b;
          box-shadow: 0 0 0 2px rgba(199, 0, 11, 0.1);
        }

        &.error {
          border-color: #c7000b;
        }
      }

      textarea {
        resize: vertical;
        min-height: 120px;
      }

      .error-message {
        color: #c7000b;
        font-size: 13px;
        margin-top: 5px;
        display: block;
      }
    }

    .submit-btn {
      width: 100%;
      padding: 14px;
      background-color: #c7000b;
      color: #fff;
      border: none;
      border-radius: 6px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.3s ease;
      position: relative;

      &:hover {
        background-color: #a00008;
      }

      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
      }

      .loading-spinner {
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 3px solid #fff;
        border-radius: 50%;
        border-top-color: transparent;
        animation: spin 1s linear infinite;
      }
    }
  }
}

.message-popup {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  min-width: 300px;
  max-width: 90%;
  animation: slideDown 0.3s ease-out;

  .message-content {
    display: flex;
    align-items: center;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background: #fff;

    &.success {
      border-left: 4px solid #52c41a;
      .message-icon {
        color: #52c41a;
      }
    }

    &.error {
      border-left: 4px solid #ff4d4f;
      .message-icon {
        color: #ff4d4f;
      }
    }
  }

  .message-icon {
    font-size: 20px;
    margin-right: 12px;
    font-weight: bold;
  }

  .message-text {
    font-size: 15px;
    color: #333;
  }
}

@keyframes slideDown {
  from {
    transform: translate(-50%, -100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

.footer {
  background-color: #1a1a1a;
  color: #fff;
  padding: 60px 0 30px;

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .footer-main {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr;
    gap: 60px;
    margin-bottom: 50px;
    
    .footer-info {
      .footer-logo {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;

        img {
          height: 40px;
          filter: brightness(0) invert(1);
        }

        .company-name {
          font-size: 20px;
          font-weight: 600;
          color: #fff;
        }
      }

      .company-slogan {
        font-size: 16px;
        color: #999;
        margin-top: 15px;
      }
    }

    h3 {
      font-size: 18px;
      color: #fff;
      margin-bottom: 20px;
      font-weight: 600;
      position: relative;
      padding-bottom: 12px;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 30px;
        height: 2px;
        background-color: #c7000b;
      }
    }

    .contact-details, .hours-details {
      p {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #999;
        margin-bottom: 12px;
        font-size: 15px;
        transition: color 0.3s;

        i {
          color: #c7000b;
          font-size: 16px;
        }

        &:hover {
          color: #fff;
        }
      }
    }
  }

  .footer-bottom {
    padding-top: 30px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;

    .copyright {
      color: #666;
      font-size: 14px;
    }

    .footer-links {
      display: flex;
      align-items: center;
      gap: 15px;

      a {
        color: #999;
        font-size: 14px;
        text-decoration: none;
        transition: color 0.3s;

        &:hover {
          color: #fff;
        }
      }

      .divider {
        color: #666;
      }
    }
  }
}

.back-to-top {
  position: fixed;
  right: 0;
  bottom: 100px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1000;

  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
}

.contact-float {
  position: fixed;
  right: 0;
  bottom: 150px;
  width: 40px;
  height: 40px;
  background: rgba(199, 0, 11, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1000;

  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
  }

  &:hover {
    background: rgba(199, 0, 11, 0.9);
  }
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.dialog-content {
  background: #fff;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  border-radius: 12px;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

.dialog-header {
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
  background: #f8f9fc;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 24px;
    color: #333;
    margin: 0;
    font-weight: 600;
    position: relative;
    padding-left: 15px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 20px;
      background-color: #c7000b;
      border-radius: 2px;
    }
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #666;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
    border-radius: 50%;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
      color: #333;
    }
  }
}

.dialog-body {
  padding: 20px;

  .contact-info {
    text-align: center;
    margin-bottom: 20px;
    padding: 0;

    h3 {
      font-size: 20px;
      color: #333;
      margin-bottom: 10px;
      font-weight: 600;
    }

    p {
      font-size: 14px;
      color: #666;
      line-height: 1.6;
      margin-bottom: 15px;
    }

    .contact-method {
      display: flex;
      justify-content: center;
      gap: 40px;
      margin-top: 15px;
      padding: 15px 20px;
      background: #f8f9fc;
      border-radius: 8px;

      .method-item {
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          width: 32px;
          height: 32px;
          background: rgba(199, 0, 11, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: #c7000b;
        }

        .method-text {
          text-align: left;

          h4 {
            font-size: 13px;
            color: #333;
            margin-bottom: 2px;
            font-weight: 600;
          }

          p {
            font-size: 12px;
            color: #666;
            margin: 0;
          }
        }
      }
    }
  }

  .contact-form {
    max-width: 600px;
    margin: 0 auto;
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

    .form-group {
      margin-bottom: 15px;
      max-width: 100%;

      label {
        display: block;
        font-size: 14px;
        color: #333;
        margin-bottom: 6px;
        font-weight: 500;

        .required {
          color: #c7000b;
          margin-left: 4px;
        }
      }

      input, textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.3s ease;
        background: #f8f9fc;
        box-sizing: border-box;

        &::placeholder {
          color: #999;
          font-size: 13px;
        }

        &:focus {
          outline: none;
          border-color: #c7000b;
          box-shadow: 0 0 0 2px rgba(199, 0, 11, 0.1);
          background: #fff;
        }

        &.error {
          border-color: #c7000b;
          background: #fff;
        }
      }

      textarea {
        resize: vertical;
        min-height: 80px;
        max-height: 120px;
      }

      .error-message {
        color: #c7000b;
        font-size: 12px;
        margin-top: 4px;
        display: block;
      }
    }

    .submit-btn {
      width: 100%;
      padding: 12px;
      background-color: #c7000b;
      color: #fff;
      border: none;
      border-radius: 6px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-top: 10px;

      &:hover {
        background-color: #a00008;
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }

      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
        transform: none;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .header {
    .nav {
      display: none;
    }

    .menu-icon {
      display: flex;
    }
  }

  .banner {
    height: calc(100vh - 60px);
    max-height: 400px;

    .banner-content {
      h2 {
        font-size: 24px;
      }

      p {
        font-size: 16px;
      }

      .learn-more {
        padding: 10px 20px;
        font-size: 14px;
      }
    }

    .banner-btn {
      display: none; // 在移动端隐藏左右按钮
    }
  }

  .recommend {
    padding: 20px 0;

    .section-title {
      font-size: 24px;
    }

    .recommend-banner {
      height: 300px;

      .content {
        h3 {
          font-size: 20px;
        }

        p {
          font-size: 14px;
        }
      }
    }

    .card-container {
      grid-template-columns: repeat(2, 1fr);
      gap: 15px;

      .card {
        img {
          height: 120px;
        }

        .card-content {
          padding: 12px;

          h3 {
            font-size: 16px;
          }

          p {
            font-size: 12px;
          }
        }
      }
    }
  }

  .company-info-section {
    .content-wrapper {
      padding: 40px 20px;
    }

    .section-title {
      font-size: 24px;
    }

    .business-intro {
      .intro-text {
        .highlight {
          font-size: 20px;
        }

        p {
          font-size: 16px;
        }
      }
    }

    .company-details {
      padding: 30px 0px;

      .info-list {
        border-radius: 8px;

        .info-item {
          padding: 18px 15px;
          flex-direction: column;

          .info-label {
            width: 100%;
            margin-bottom: 8px;
            padding-left: 12px;
            font-size: 14px;

            &::before {
              width: 4px;
              height: 4px;
            }
          }

          .info-content {
            font-size: 14px;
            line-height: 1.6;
            padding-left: 12px;

            &::before {
              display: none;
            }
          }
        }
      }

      .company-photos {
        margin-top: 30px;

        .photo-grid {
          grid-template-columns: 1fr;
          gap: 15px;

          .photo-item {
            img {
              height: 200px;
            }

            .photo-caption {
              padding: 12px;
              font-size: 14px;
            }
          }
        }
      }
    }
  }

  .products-section {
    padding: 40px 0;

    .product-category {
      margin-bottom: 40px;
      padding: 0 15px;
    }

    .section-title {
      font-size: 24px;
      margin-bottom: 30px;
    }

    .product-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .product-card {
      .product-image {
        img {
          width: calc(100% - 30px);
          height: calc(100% - 30px);
          padding: 15px;
        }
      }

      .product-info {
        padding: 15px;

        .product-name {
          font-size: 18px;
        }

        .product-desc {
          font-size: 13px;
          margin-bottom: 10px;
        }

        .product-price {
          font-size: 16px;
        }
      }
    }
  }

  .contact-section {
    padding: 40px 0;

    .contact-container {
      grid-template-columns: 1fr;
      gap: 30px;
    }

    .contact-info {
      text-align: center;

      h3 {
        font-size: 20px;
      }

      p {
        font-size: 14px;
      }

      .contact-method {
        display: flex;
        justify-content: center;
        gap: 20px;

        .method-item {
          margin-bottom: 0;
        }
      }
    }

    .contact-form {
      padding: 20px;

      .form-group {
        margin-bottom: 20px;

        label {
          font-size: 14px;
        }

        input, textarea {
          padding: 10px;
          font-size: 14px;
        }
      }

      .submit-btn {
        padding: 12px;
        font-size: 15px;
      }
    }
  }

  .message-popup {
    min-width: 280px;
    
    .message-content {
      padding: 12px 16px;
      
      .message-text {
        font-size: 14px;
      }
    }
  }

  .footer {
    padding: 40px 0 20px;

    .footer-main {
      grid-template-columns: 1fr;
      gap: 30px;
      margin-bottom: 30px;
      text-align: center;

      .footer-info {
        .footer-logo {
          justify-content: center;
          margin-bottom: 15px;

          img {
            height: 32px;
          }

          .company-name {
            font-size: 18px;
          }
        }
      }

      h3 {
        font-size: 16px;
        margin-bottom: 15px;

        &::after {
          left: 50%;
          transform: translateX(-50%);
        }
      }

      .contact-details, .hours-details {
        p {
          justify-content: center;
          font-size: 14px;
          margin-bottom: 10px;
        }
      }
    }

    .footer-bottom {
      flex-direction: column;
      gap: 15px;
      text-align: center;

      .copyright {
        order: 2;
      }

      .footer-links {
        order: 1;
        flex-wrap: wrap;
        justify-content: center;
      }
    }
  }

  .back-to-top {
    width: 36px;
    height: 36px;
    bottom: 30px;

    img {
      width: 18px;
      height: 18px;
    }
  }

  .contact-float {
    width: 36px;
    height: 36px;
    bottom: 80px;

    img {
      width: 18px;
      height: 18px;
    }
  }

  .dialog-content {
    width: 95%;
    max-height: 95vh;
  }

  .dialog-header {
    padding: 15px 20px;
  }

  .dialog-body {
    padding: 15px;

    .contact-info {
      margin-bottom: 20px;
      padding: 0;

      h3 {
        font-size: 18px;
      }

      p {
        font-size: 13px;
      }

      .contact-method {
        flex-direction: row;
        justify-content: flex-start;
        gap: 20px;
        padding: 12px 15px;
      }
    }

    .contact-form {
      padding: 15px;

      .form-group {
        margin-bottom: 12px;

        input, textarea {
          padding: 8px 10px;
        }
      }

      .submit-btn {
        padding: 10px;
        font-size: 14px;
      }
    }
  }
}
</style> 